import { useEffect, useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from 'recharts';
import { supabase } from '@shared/lib/supabase';

interface ProviderStat {
  provider: string;
  orders_total: number;
  orders_completed: number;
  orders_failed: number;
  volume_usd: number;
}

export default function CryptoProviders() {
  const [stats, setStats] = useState<ProviderStat[]>([]);
  const [loading, setLoading] = useState(true);
  const [chartView, setChartView] = useState<'volume' | 'success'>('volume');

  useEffect(() => {
    async function load() {
      const { data } = await supabase
        .from('provider_stats')
        .select('provider, orders_total, orders_completed, orders_failed, volume_usd')
        .order('date', { ascending: false })
        .limit(100);

      if (data) {
        const grouped = data.reduce<Record<string, ProviderStat>>((acc, row) => {
          const key = row.provider;
          if (!acc[key]) {
            acc[key] = { provider: key, orders_total: 0, orders_completed: 0, orders_failed: 0, volume_usd: 0 };
          }
          acc[key].orders_total += row.orders_total;
          acc[key].orders_completed += row.orders_completed;
          acc[key].orders_failed += row.orders_failed;
          acc[key].volume_usd += Number(row.volume_usd);
          return acc;
        }, {});
        setStats(Object.values(grouped));
      }
      setLoading(false);
    }
    load();
  }, []);

  if (loading) {
    return (
      <div className="p-6">
        <h1 className="font-sans text-lg font-medium mb-5" style={{ color: 'var(--dash-text-primary)' }}>Providers</h1>
        <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>Loading provider data...</p>
      </div>
    );
  }

  const successRateData = stats.map((s) => ({
    provider: s.provider,
    'Success %': s.orders_total > 0 ? Number(((s.orders_completed / s.orders_total) * 100).toFixed(1)) : 0,
    'Failure %': s.orders_total > 0 ? Number(((s.orders_failed / s.orders_total) * 100).toFixed(1)) : 0,
  }));

  return (
    <div className="p-6">
      <h1 className="font-sans text-lg font-medium mb-5" style={{ color: 'var(--dash-text-primary)' }}>Providers</h1>

      {stats.length === 0 ? (
        <div className="rounded p-8 text-center" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
          <p className="font-sans text-sm" style={{ color: 'var(--dash-text-dim)' }}>
            No provider data yet. Stats will appear after your first transaction.
          </p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            {stats.map((s) => {
              const rate = s.orders_total > 0 ? (s.orders_completed / s.orders_total) * 100 : 0;
              const rateColor = rate >= 90 ? 'text-emerald-400' : rate >= 70 ? 'text-amber-400' : 'text-red-400';
              return (
                <div
                  key={s.provider}
                  className="rounded p-4 transition-colors"
                  style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--dash-hover-bg)'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = 'var(--dash-card)'; }}
                >
                  <div className="font-sans text-sm font-medium mb-3" style={{ color: 'var(--dash-text-secondary)' }}>{s.provider}</div>
                  <dl className="space-y-2 text-xs font-sans">
                    <div className="flex justify-between">
                      <dt style={{ color: 'var(--dash-text-dim)' }}>Total Orders</dt>
                      <dd className="font-mono" style={{ color: 'var(--dash-text-tertiary)' }}>{s.orders_total}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt style={{ color: 'var(--dash-text-dim)' }}>Completed</dt>
                      <dd className="font-mono" style={{ color: 'var(--dash-text-tertiary)' }}>{s.orders_completed}</dd>
                    </div>
                    <div className="flex justify-between">
                      <dt style={{ color: 'var(--dash-text-dim)' }}>Success Rate</dt>
                      <dd className={`font-mono ${rateColor}`}>
                        {rate.toFixed(1)}%
                      </dd>
                    </div>
                    <div className="flex justify-between">
                      <dt style={{ color: 'var(--dash-text-dim)' }}>Volume</dt>
                      <dd className="font-mono" style={{ color: 'var(--dash-text-tertiary)' }}>${s.volume_usd.toFixed(2)}</dd>
                    </div>
                  </dl>
                  <div className="mt-3 pt-3" style={{ borderTop: '1px solid var(--dash-border-light)' }}>
                    <div className="w-full rounded-full h-1.5 overflow-hidden" style={{ background: 'var(--dash-hover-bg)' }}>
                      <div
                        className={`h-full rounded-full transition-all ${
                          rate >= 90 ? 'bg-emerald-500' : rate >= 70 ? 'bg-amber-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${rate}%` }}
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="rounded p-6" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'var(--dash-text-dim)' }}>
                {chartView === 'volume' ? 'Volume by Provider' : 'Success Rate Comparison'}
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => setChartView('volume')}
                  className="px-2 py-1 rounded font-mono text-[10px] border transition-colors"
                  style={
                    chartView === 'volume'
                      ? { borderColor: 'var(--dash-border)', background: 'var(--dash-hover-bg)', color: 'var(--dash-text-primary)' }
                      : { borderColor: 'var(--dash-border)', color: 'var(--dash-text-dim)' }
                  }
                  onMouseEnter={(e) => { if (chartView !== 'volume') e.currentTarget.style.color = 'var(--dash-text-tertiary)'; }}
                  onMouseLeave={(e) => { if (chartView !== 'volume') e.currentTarget.style.color = 'var(--dash-text-dim)'; }}
                >
                  Volume
                </button>
                <button
                  onClick={() => setChartView('success')}
                  className="px-2 py-1 rounded font-mono text-[10px] border transition-colors"
                  style={
                    chartView === 'success'
                      ? { borderColor: 'var(--dash-border)', background: 'var(--dash-hover-bg)', color: 'var(--dash-text-primary)' }
                      : { borderColor: 'var(--dash-border)', color: 'var(--dash-text-dim)' }
                  }
                  onMouseEnter={(e) => { if (chartView !== 'success') e.currentTarget.style.color = 'var(--dash-text-tertiary)'; }}
                  onMouseLeave={(e) => { if (chartView !== 'success') e.currentTarget.style.color = 'var(--dash-text-dim)'; }}
                >
                  Success Rate
                </button>
              </div>
            </div>
            <ResponsiveContainer width="100%" height={240}>
              {chartView === 'volume' ? (
                <BarChart data={stats}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis
                    dataKey="provider"
                    tick={{ fontSize: 11, fontFamily: '"IBM Plex Sans"', fill: '#71717a' }}
                    stroke="#27272a"
                  />
                  <YAxis
                    tick={{ fontSize: 10, fontFamily: '"IBM Plex Mono"', fill: '#71717a' }}
                    stroke="#27272a"
                    tickFormatter={(v) => `$${v}`}
                  />
                  <Tooltip
                    contentStyle={{
                      background: '#141417',
                      border: '1px solid #27272a',
                      borderRadius: '4px',
                      fontFamily: '"IBM Plex Mono"',
                      fontSize: 12,
                      color: '#e4e4e7',
                    }}
                    labelStyle={{ color: '#71717a' }}
                    formatter={(value) => [`$${Number(value).toFixed(2)}`, 'Volume']}
                  />
                  <Bar dataKey="volume_usd" fill="#3b82f6" radius={[2, 2, 0, 0]} />
                </BarChart>
              ) : (
                <BarChart data={successRateData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                  <XAxis
                    dataKey="provider"
                    tick={{ fontSize: 11, fontFamily: '"IBM Plex Sans"', fill: '#71717a' }}
                    stroke="#27272a"
                  />
                  <YAxis
                    tick={{ fontSize: 10, fontFamily: '"IBM Plex Mono"', fill: '#71717a' }}
                    stroke="#27272a"
                    tickFormatter={(v) => `${v}%`}
                    domain={[0, 100]}
                  />
                  <Tooltip
                    contentStyle={{
                      background: '#141417',
                      border: '1px solid #27272a',
                      borderRadius: '4px',
                      fontFamily: '"IBM Plex Mono"',
                      fontSize: 12,
                      color: '#e4e4e7',
                    }}
                    labelStyle={{ color: '#71717a' }}
                    formatter={(value) => [`${value}%`]}
                  />
                  <Legend
                    wrapperStyle={{ fontFamily: '"IBM Plex Mono"', fontSize: 11, color: '#a1a1aa' }}
                  />
                  <Bar dataKey="Success %" fill="#34d399" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="Failure %" fill="#f87171" radius={[2, 2, 0, 0]} />
                </BarChart>
              )}
            </ResponsiveContainer>
          </div>
        </>
      )}
    </div>
  );
}
